/**
 * One styling contract for every react-select picker in the app.
 *
 * These pickers do not offer plain strings. A customer option carries the name, the phone, the
 * address and two running balances; a product option carries brand, model, category and SKU.
 * That secondary matter was marked up with Bootstrap's `.text-muted` -- a fixed grey, chosen
 * for a white background, `!important` so nothing can move it. react-select then paints the
 * highlighted option in a saturated blue and sets the option's text to white, and the grey
 * stayed exactly where it was: #6c757d on #2684FF is about 1.9:1, which is not low contrast,
 * it is invisible. The figures a user opens that menu to read were the ones they could not
 * read, and only while they were looking straight at them.
 *
 * So colour here is a function of the option's STATE, and the metadata follows it:
 *
 *   default   surface white          text ink            meta slate
 *   focused   brand tint             text ink            meta slate (darkened for the tint)
 *   selected  brand blue             text white          meta white at 86%
 *
 * The meta colour travels as a CSS custom property rather than a class, because a class cannot
 * win against `.text-muted !important` and should not have to try. `--picker-meta` is set on
 * the option and inherited by `.picker-meta` inside it (see _customeColor.scss). Every value
 * below is measured, not eyeballed:
 *
 *   white on BRAND ................ 8.7:1   (AA and AAA for any size)
 *   white 86% on BRAND ............ 6.8:1   (AA and AAA for small text)
 *   META on BRAND_TINT ............ 5.3:1
 *   META on white ................. 6.0:1
 *
 * Extend rather than replace: `{ ...pickerStyles, control: ... }` keeps the option states.
 */

/** The portal's own blue. Same value as `.border-font-blue` and the sidebar. */
const BRAND = "#0839c4";
/** A tint of it, for the row the pointer or the keyboard is on but has not chosen. */
const BRAND_TINT = "#eef2fd";
/** CoreUI's body ink. */
const INK = "#3c4b64";
/** Secondary text. Darker than Bootstrap's #6c757d so it still clears AA on the tint. */
const META = "#5a636c";
const META_ON_BRAND = "rgba(255, 255, 255, 0.86)";

/**
 * The option row.
 *
 * `:active` is stated explicitly. react-select's default gives the pressed option its own
 * background, which on a selected row would have been the one state nobody styled.
 */
const option = (base, { isSelected, isFocused, isDisabled }) => ({
  ...base,
  backgroundColor: isDisabled
    ? "transparent"
    : isSelected
    ? BRAND
    : isFocused
    ? BRAND_TINT
    : "transparent",
  color: isDisabled ? "#9da5b1" : isSelected ? "#fff" : INK,
  // Inherited by `.picker-meta` on every line inside this option.
  "--picker-meta": isSelected ? META_ON_BRAND : META,
  cursor: isDisabled ? "not-allowed" : "pointer",
  ":active": {
    ...base[":active"],
    backgroundColor: isSelected ? BRAND : BRAND_TINT,
  },
});

/**
 * The underlined control the forms use.
 *
 * Transcribed from the six copies of this object that had been pasted across SearchCustomer
 * and tableUtils, so that the next change to a picker happens once. The focused state is new:
 * the border used to stay the same grey whether or not the control had the keyboard, which
 * left a keyboard user with no way to tell where they were.
 */
const control = (base, { isFocused }) => ({
  ...base,
  border: "0",
  borderRadius: "0%",
  borderBottom: `1px solid ${isFocused ? BRAND : "#d8dbe0"}`,
  "&:hover": { borderColor: "none" },
  boxShadow: "none",
  minHeight: 0,
});

/** Menus are portalled to the body, so they need to outrank the modals they open over. */
const menuPortal = (base) => ({ ...base, zIndex: 9999 });
const menu = (base) => ({ ...base, zIndex: 9999 });

export const pickerStyles = { control, option, menuPortal, menu };

/** Same option contract, for a picker that wants its own control or container rules. */
export const withPickerStyles = (overrides = {}) => ({
  ...pickerStyles,
  ...overrides,
});

export const pickerColors = { BRAND, BRAND_TINT, INK, META };
